import React, { useEffect, useMemo, useState, useContext } from "react";
import { Card, FormLayout, TextField, EmptyState, Select, Text, Stack } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../../../components/global/appContext";
import useLanguageSelector from "../../../../../hooks/useLanguageSelector";
import LanguageSelector from "../../../../../components/components/LanguageSelector";

import contentTypeOptions from "../../../../settings/pages/customFields/contentTypeOptions";

export default function CustomFields(props) {
  const { location, setLocation } = props;
  const { t } = useTranslation();
  const { languageTranslateIconUrl } = useContext(AppContext);
  
  // Use the new language selector hook
  const {
    currentLocale,
    isLocalizationEnabled,
    localeOptions,
    getLocaleDisplayName
  } = useLanguageSelector(location);

  const [selectedLocale, setSelectedLocale] = useState("en");

  useEffect(() => {
    if (currentLocale && selectedLocale === "en") {
      setSelectedLocale(currentLocale);
    }
  }, [currentLocale]);

  function handleFieldUpdate(field, newValue) {
    setLocation((oldLocation) => {
      // Update the field we are editing
      const fields = oldLocation.fields.map((f) => {
        if (f.id === field.id) {
          // Handle translations
          if (selectedLocale === (oldLocation.current_locale || "en")) {
            // Default locale - update the main content field
            return { ...f, content: newValue };
          } else {
            // Non-default locale - update translations
            const newTranslations = f.content_translations ? { ...f.content_translations } : {};
            newTranslations[selectedLocale] = newValue;
            return { ...f, content_translations: newTranslations };
          }
        }
        // Return unchanged fields
        return f;
      });
      return { ...oldLocation, fields, field_contents_attributes: {} };
    });
  }

  useEffect(() => {
    // When we update fields, we need to update the field_contents_attributes
    // So that rails can update the locations field_contents
    // Using accepts_nested_attributes_for :field_contents
    const newLocation = { ...location };

    // If we don't have any field_contents_attributes, we don't need to do anything
    // They will be created if we update a field
    if (!newLocation.field_contents_attributes) return;
    
    // Create field contents attributes from fields
    newLocation.fields.forEach((field) => {
      const fieldContentAttr = {
        id: field.content_id,
        content: field.content,
        field_id: field.id,
      };
      
      // Add translations if they exist
      if (field.content_translations) {
        fieldContentAttr.translations = { content: field.content_translations };
      }
      
      newLocation.field_contents_attributes[field.id] = fieldContentAttr;
    });
    setLocation(newLocation);
  }, [location.fields]);

  function getFieldValue(field) {
    if (!location || !field) return "";
    
    const currentLocale = location?.current_locale || "en";
    
    if (selectedLocale === currentLocale) {
      // Default locale - return main content
      return field.content || "";
    } else {
      // Non-default locale - return translation or empty string
      return field.content_translations?.[selectedLocale] || "";
    }
  }

  function getPlaceholderText(field) {
    if (!location || !field) return "";
    
    const currentLocale = location?.current_locale || "en";
    
    if (selectedLocale === currentLocale) {
      return "";
    }
    
    // Show default content as placeholder
    const defaultContent = field.content || "";
    return defaultContent ? t("common.default", { value: defaultContent }) : "";
  }

  const isDefaultLocale = selectedLocale === currentLocale;

  const fieldMarkup = (location?.fields || []).map((field) => {
    const { id, name, content_type } = field;
    const contentType = contentTypeOptions(t).find(
      (ct) => ct.value === content_type
    );

    return (
      <TextField
        key={id}
        label={name}
        value={getFieldValue(field)}
        placeholder={getPlaceholderText(field)}
        {...(contentType?.textFieldOptions || {})}
        onChange={(newContent) => handleFieldUpdate(field, newContent)}
      />
    );
  });

  // Language selector component for the card header
  const languageSelector = (
    <LanguageSelector
      options={localeOptions}
      value={selectedLocale}
      onChange={setSelectedLocale}
    />
  );

  const cardActions = [
    { content: t("location.customFields.configureFields"), url: "/settings/fields" }
  ];

  return (
    <Card
      title={
        <Stack distribution="equalSpacing" alignment="center">
          <Text variant="headingMd" as="h2">{t("location.customFields.title")}</Text>
          {languageSelector}
        </Stack>
      }
      actions={cardActions}
    >
      {isLocalizationEnabled && !isDefaultLocale && (
        <Card.Section>
          <Text variant="bodyMd" color="subdued">
            {t("seo.editingLocaleHelp", { 
              locale: getLocaleDisplayName(selectedLocale),
              defaultLocale: getLocaleDisplayName(currentLocale)
            })}
          </Text>
        </Card.Section>
      )}
      <Card.Section>
        <FormLayout>
          {(location?.fields || []).length ? (
            fieldMarkup
          ) : (
            <EmptyState
              heading={t("location.customFields.noCustomFields")}
              action={{ content: t("common.add"), url: "/settings/fields/new" }}
            >
              <p>
                {t("location.customFields.emptyState")}
              </p>
            </EmptyState>
          )}
        </FormLayout>
      </Card.Section>
    </Card>
  );
}
